import { useState, useEffect } from "react";
import scanStore from "@/store/scanStore";

const STATUS_CONFIG = {
  unassigned: {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect x="6" y="6" width="36" height="36" rx="8" stroke="#94a3b8" strokeWidth="2.5" strokeDasharray="6 4" />
        <circle cx="24" cy="20" r="5" stroke="#94a3b8" strokeWidth="2" />
        <path d="M15 36c0-5 4-9 9-9s9 4 9 9" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        <line x1="34" y1="10" x2="40" y2="16" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        <line x1="40" y1="10" x2="34" y2="16" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    title: "Chip mới",
    description: "Chip mới. Vui lòng liên hệ nhà cung cấp để được hỗ trợ kích hoạt.",
    color: "#94a3b8",
    bgGlow: "rgba(148, 163, 184, 0.08)",
    bgAccent: "rgba(148, 163, 184, 0.04)",
    badge: "UNASSIGNED",
  },
  assigned: {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect x="6" y="6" width="36" height="36" rx="8" stroke="#f59e0b" strokeWidth="2.5" />
        <path d="M18 24h12M24 18v12" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="37" cy="11" r="5" fill="#f59e0b" opacity="0.2" stroke="#f59e0b" strokeWidth="1.5" />
        <path d="M35 11h4M37 9v4" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    title: "Chip đang chờ thiết lập",
    description:
      "Chip đã được gán cho doanh nghiệp nhưng chưa sẵn sàng sử dụng. Doanh nghiệp cần hoàn tất cấu hình trước khi chip có thể hoạt động.",
    color: "#f59e0b",
    bgGlow: "rgba(245, 158, 11, 0.08)",
    bgAccent: "rgba(245, 158, 11, 0.04)",
    badge: "ASSIGNED",
  },
  locked: {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect x="10" y="22" width="28" height="20" rx="4" stroke="#f43f5e" strokeWidth="2.5" />
        <path d="M16 22v-6a8 8 0 0 1 16 0v6" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="24" cy="33" r="3" fill="#f43f5e" />
        <path d="M24 36v3" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    title: "Chip đã bị khoá",
    description:
      "Chip này đã bị khoá do phát hiện hoạt động bất thường hoặc vi phạm chính sách. Liên hệ bộ phận hỗ trợ để được xử lý.",
    color: "#f43f5e",
    bgGlow: "rgba(244, 63, 94, 0.08)",
    bgAccent: "rgba(244, 63, 94, 0.04)",
    badge: "LOCKED",
  },
  disabled: {
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="18" stroke="#6b7280" strokeWidth="2.5" />
        <line x1="11.27" y1="36.73" x2="36.73" y2="11.27" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" />
        <rect x="18" y="18" width="12" height="12" rx="2" stroke="#6b7280" strokeWidth="1.5" opacity="0.5" />
      </svg>
    ),
    title: "Chip đã bị vô hiệu hoá",
    description:
      "Chip này đã bị vô hiệu hoá vĩnh viễn và không thể sử dụng. Nếu bạn cho rằng đây là nhầm lẫn, vui lòng liên hệ hỗ trợ.",
    color: "#6b7280",
    bgGlow: "rgba(107, 114, 128, 0.06)",
    bgAccent: "rgba(107, 114, 128, 0.03)",
    badge: "DISABLED",
  },
};

const ERROR_STATUSES = ["unassigned", "assigned", "locked", "disabled"];

const ChipScanError = () => {
  const [mounted, setMounted] = useState(false);
  const { chip, serial, company } = scanStore();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (!chip?.status || !ERROR_STATUSES.includes(chip.status)) return null;

  const config = STATUS_CONFIG[chip?.status as keyof typeof STATUS_CONFIG];

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        background: "#fafbfc",
        fontFamily: "'Outfit', 'Segoe UI', system-ui, -apple-system, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        @keyframes iconBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes ringPulse {
          0% { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        .error-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 24px;
          border: 1px solid #e8ecf1;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 8px 32px rgba(0,0,0,0.06);
          overflow: hidden;
          opacity: 0;
          animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .error-header {
          position: relative;
          padding: 40px 32px 32px;
          text-align: center;
          overflow: hidden;
        }

        .error-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: ${config.color};
        }

        .error-header::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(ellipse at top, ${config.bgGlow}, transparent 70%);
          pointer-events: none;
        }

        .icon-container {
          position: relative;
          width: 88px;
          height: 88px;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }

        .icon-bg {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: ${config.bgAccent};
          border: 1.5px solid ${config.color}18;
          animation: iconBreathe 3s ease-in-out infinite;
        }

        .icon-ring {
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 1.5px solid ${config.color}20;
          animation: ringPulse 2.5s ease-out infinite;
        }

        .icon-inner {
          position: relative;
          z-index: 1;
          opacity: 0;
          animation: fadeUp 0.5s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          border-radius: 100px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 1.5px;
          color: ${config.color};
          background: ${config.bgAccent};
          border: 1px solid ${config.color}20;
          margin-bottom: 16px;
          position: relative;
          z-index: 1;
          opacity: 0;
          animation: fadeUp 0.5s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: ${config.color};
          animation: pulse 2s ease-in-out infinite;
        }

        .error-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 10px;
          line-height: 1.3;
          position: relative;
          z-index: 1;
          opacity: 0;
          animation: fadeUp 0.5s 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .error-desc {
          font-size: 14.5px;
          font-weight: 400;
          color: #64748b;
          line-height: 1.65;
          margin: 0;
          position: relative;
          z-index: 1;
          opacity: 0;
          animation: fadeUp 0.5s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .error-details {
          padding: 0 24px 24px;
          opacity: 0;
          animation: fadeUp 0.5s 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .detail-box {
          background: #f8fafc;
          border: 1px solid #e8ecf1;
          border-radius: 14px;
          padding: 16px 18px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .detail-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .detail-label {
          font-size: 12.5px;
          color: #94a3b8;
          font-weight: 500;
          white-space: nowrap;
        }

        .detail-value {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12.5px;
          color: #334155;
          font-weight: 500;
          text-align: right;
          word-break: break-all;
        }

        .detail-divider {
          height: 1px;
          background: #e8ecf1;
          margin: 2px 0;
        }

        .error-actions {
          padding: 0 24px 28px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          opacity: 0;
          animation: fadeUp 0.5s 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px 20px;
          border-radius: 14px;
          border: none;
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #fff;
          background: ${config.color};
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .btn-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          background-size: 200% 100%;
          animation: shimmer 2.5s ease-in-out infinite;
        }

        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px ${config.color}40;
        }

        .btn-primary:active {
          transform: translateY(0);
        }

        .btn-secondary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 13px 20px;
          border-radius: 14px;
          border: 1.5px solid #e2e8f0;
          background: transparent;
          font-family: 'Outfit', sans-serif;
          font-size: 14.5px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .btn-secondary:active {
          background: #f1f5f9;
        }
      `}</style>

      <div className="error-card" style={{ animationDelay: mounted ? "0s" : "0.1s" }}>
        {/* Header */}
        <div className="error-header">
          <div className="icon-container">
            <div className="icon-ring" />
            <div className="icon-bg" />
            <div className="icon-inner">{config.icon}</div>
          </div>

          <div className="badge">
            <span className="badge-dot" />
            {config.badge}
          </div>

          <h1 className="error-title">{config.title}</h1>
          <p className="error-desc">{config.description}</p>
        </div>

        {/* Details */}
        <div className="error-details">
          <div className="detail-box">
            {serial && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Serial</span>
                  <span className="detail-value">{serial}</span>
                </div>
                <div className="detail-divider" />
              </>
            )}
            <div className="detail-row">
              <span className="detail-label">Trạng thái</span>
              <span className="detail-value" style={{ color: config.color }}>
                {config.badge}
              </span>
            </div>
            {company && company.name && (
              <>
                <div className="detail-divider" />
                <div className="detail-row">
                  <span className="detail-label">Doanh nghiệp</span>
                  <span className="detail-value">{company.name}</span>
                </div>
              </>
            )}
            <div className="detail-divider" />
            <div className="detail-row">
              <span className="detail-label">Thời gian</span>
              <span className="detail-value">
                {new Date().toLocaleString("vi-VN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="error-actions">
          {/* <button className="btn-primary">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Liên hệ hỗ trợ
          </button> */}
          <button className="btn-primary" onClick={() => window.location.reload()}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Thử lại
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChipScanError;
